export type ApplePayDebugLevel = "info" | "warn" | "error";

export interface ApplePayDebugEntry {
  id: number;
  time: string;
  level: ApplePayDebugLevel;
  message: string;
  data?: Record<string, unknown>;
}

const STORAGE_KEY = "apple_pay_debug";
const QUERY_KEY = "apdebug";
const MAX_ENTRIES = 300;

let entries: ApplePayDebugEntry[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

const notify = () => {
  listeners.forEach((listener) => listener());
};

/**
 * The debug panel is shown when the page is opened with `?apdebug=1`. The flag
 * is remembered in localStorage so it survives navigation and redirects.
 * `?apdebug=0` (or the panel's Off button) turns it off again.
 */
export const isApplePayDebugEnabled = (): boolean => {
  try {
    const param = new URLSearchParams(window.location.search).get(QUERY_KEY);
    if (param === "1") {
      window.localStorage.setItem(STORAGE_KEY, "1");
      return true;
    }
    if (param === "0") {
      window.localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

export const setApplePayDebugEnabled = (enabled: boolean) => {
  try {
    if (enabled) window.localStorage.setItem(STORAGE_KEY, "1");
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // localStorage unavailable; nothing to persist
  }
  notify();
};

const sanitize = (value: unknown, depth = 0): unknown => {
  if (value === null || value === undefined) return value;
  if (value instanceof Error) {
    return { name: value.name, message: value.message };
  }
  if (typeof value === "function") return "[function]";
  if (typeof value !== "object") return value;
  if (depth > 3) return "[nested]";
  if (Array.isArray(value))
    return value.map((item) => sanitize(item, depth + 1));
  const result: Record<string, unknown> = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
    result[key] = sanitize(item, depth + 1);
  });
  return result;
};

export const applePayDebug = (
  level: ApplePayDebugLevel,
  message: string,
  data?: Record<string, unknown>
) => {
  const entry: ApplePayDebugEntry = {
    id: nextId++,
    time: new Date().toISOString().slice(11, 23),
    level,
    message,
    data: data ? (sanitize(data) as Record<string, unknown>) : undefined,
  };
  entries = [...entries, entry].slice(-MAX_ENTRIES);
  notify();
};

export const clearApplePayDebug = () => {
  entries = [];
  notify();
};

export const getApplePayDebugEntries = () => entries;

export const subscribeApplePayDebug = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const formatApplePayDebugEntries = (list: ApplePayDebugEntry[]) =>
  list
    .map((entry) => {
      const data = entry.data ? ` ${JSON.stringify(entry.data)}` : "";
      return `${entry.time} [${entry.level.toUpperCase()}] ${entry.message}${data}`;
    })
    .join("\n");

/** Keeps the intent id visible but hides the secret half of a client_secret. */
export const maskClientSecret = (value: unknown) => {
  if (typeof value !== "string" || !value) return value;
  const secretIndex = value.indexOf("_secret_");
  return secretIndex > 0
    ? `${value.slice(0, secretIndex)}_secret_…`
    : `${value.slice(0, 12)}…`;
};

/** Summarises a Stripe PaymentIntent or SetupIntent for the debug panel. */
export const summarizeStripeIntent = (intent: any) => ({
  id: intent?.id,
  status: intent?.status,
  amount: intent?.amount,
  currency: intent?.currency,
  paymentMethod:
    typeof intent?.payment_method === "string"
      ? intent.payment_method
      : intent?.payment_method?.id,
  captureMethod: intent?.capture_method,
  nextActionType: intent?.next_action?.type,
  cancellationReason: intent?.cancellation_reason,
  lastError:
    intent?.last_payment_error || intent?.last_setup_error
      ? {
          code: (intent.last_payment_error || intent.last_setup_error)?.code,
          declineCode: (intent.last_payment_error || intent.last_setup_error)
            ?.decline_code,
          message: (intent.last_payment_error || intent.last_setup_error)
            ?.message,
          type: (intent.last_payment_error || intent.last_setup_error)?.type,
        }
      : undefined,
});

/**
 * Records uncaught errors and unhandled promise rejections while debugging so
 * a crash elsewhere on the page (for example in the parent payment handler) is
 * visible next to the Apple Pay events. Returns a cleanup function.
 */
export const installApplePayGlobalErrorCapture = () => {
  const onError = (event: ErrorEvent) => {
    applePayDebug("error", "uncaught page error", {
      message: event.message,
      source: event.filename
        ? `${event.filename}:${event.lineno}:${event.colno}`
        : undefined,
    });
  };
  const onRejection = (event: PromiseRejectionEvent) => {
    const reason: any = event.reason;
    applePayDebug("error", "unhandled promise rejection", {
      message: reason?.message ?? String(reason),
      name: reason?.name,
      httpStatus: reason?.response?.status,
      responseBody: reason?.response?.data,
    });
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };
};

export const getApplePayEnvironment = () => {
  const applePaySession = (window as any).ApplePaySession;
  let nativeCanMakePayments: boolean | string | null = null;
  try {
    nativeCanMakePayments = applePaySession
      ? Boolean(applePaySession.canMakePayments())
      : null;
  } catch (error) {
    nativeCanMakePayments = `threw: ${
      error instanceof Error ? error.message : String(error)
    }`;
  }

  let applePaySessionVersion: number | null = null;
  if (typeof applePaySession?.supportsVersion === "function") {
    for (let version = 1; version <= 20; version += 1) {
      try {
        if (applePaySession.supportsVersion(version)) {
          applePaySessionVersion = version;
        }
      } catch {
        break;
      }
    }
  }

  return {
    applePaySessionAvailable: Boolean(applePaySession),
    applePaySessionVersion,
    nativeCanMakePayments,
    host: window.location.host,
    protocol: window.location.protocol,
    isSecureContext: window.isSecureContext,
    isFramed: window.self !== window.top,
    paymentRequestSupported: "PaymentRequest" in window,
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
  };
};
